/**
 * MarkAsAppliedModal — lightweight dialog for recording an application
 * from the Postings page. Pre-fills company and job title from the posting.
 * Status is always seeded as 'applied'; only the application date is asked for.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { applicationApi } from '@/lib/api';
import type { CreateApplication } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function today(): string {
  return new Date().toISOString().split('T')[0]!;
}

interface MarkAsAppliedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postingId: string;
  defaultCompany?: string | null;
  defaultJobTitle?: string | null;
}

export function MarkAsAppliedModal({
  open,
  onOpenChange,
  postingId,
  defaultCompany,
  defaultJobTitle,
}: MarkAsAppliedModalProps) {
  const queryClient = useQueryClient();

  const [company, setCompany] = useState(defaultCompany ?? '');
  const [jobTitle, setJobTitle] = useState(defaultJobTitle ?? '');
  const [appliedDate, setAppliedDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setCompany(defaultCompany ?? '');
      setJobTitle(defaultJobTitle ?? '');
      setAppliedDate(today());
      setNotes('');
      setError('');
    }
    onOpenChange(next);
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateApplication) => applicationApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.trim()) { setError('Company is required'); return; }
    if (!jobTitle.trim()) { setError('Job title is required'); return; }
    setError('');
    createMutation.mutate({
      postingId,
      company: company.trim(),
      jobTitle: jobTitle.trim(),
      appliedDate,
      lastAction: 'applied',
      lastActionDate: appliedDate,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Mark as Applied</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="maa-company">Company *</Label>
              <Input
                id="maa-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company name"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maa-title">Job Title *</Label>
              <Input
                id="maa-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Job title"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="maa-applied">Date Applied *</Label>
            <Input
              id="maa-applied"
              type="date"
              value={appliedDate}
              onChange={(e) => setAppliedDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="maa-notes">Notes</Label>
            <Input
              id="maa-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : 'Save Application'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
